import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DecimalTextField, { DecimalTextFieldProps } from '../../../../components/DecimalTextField';
import { resolveDecimalFieldPolicy } from '../../../../utils/decimalInput';

const ESAL_GRID_POLICY = resolveDecimalFieldPolicy({}, 'esalGrid');

interface EsalNumberCellProps {
    /** `row:field`, the key the owner tracks in its set of invalid cells */
    cellKey: string;
    fieldLabel: string;
    value: number;
    disabled: boolean;
    placeholder?: string;
    inputProps?: DecimalTextFieldProps['inputProps'];
    resetKey?: string | number;
    onValueChange: (value: number) => void;
    onValidityChange?: (key: string, isValid: boolean) => void;
}

/**
 * Editable numeric cell of the ESAL truck and aircraft grids. Counts and loads cannot be negative:
 * a negative number stays visible with an error and keeps the cell invalid instead of being dropped.
 */
const EsalNumberCell: React.FC<EsalNumberCellProps> = ({ cellKey, fieldLabel, value, disabled, placeholder, inputProps, resetKey, onValueChange, onValidityChange }) => {
    const { t } = useTranslation();
    // The negative number the user typed: shown instead of the stored value until it is corrected
    const [rejectedValue, setRejectedValue] = useState<number | null>(null);
    const [seenResetKey, setSeenResetKey] = useState(resetKey);

    if (resetKey !== seenResetKey) {
        setSeenResetKey(resetKey);
        setRejectedValue(null);
    }

    const handleValueChange = (typedValue: number | null) => {
        const numericValue = typedValue ?? 0;
        const belowZero = numericValue < 0;
        setRejectedValue(belowZero ? numericValue : null);
        onValidityChange?.(cellKey, !belowZero);
        if (!belowZero) onValueChange(numericValue);
    };

    return (
        <DecimalTextField
            value={rejectedValue ?? (value || '')}
            policy={ESAL_GRID_POLICY}
            emptyPolicy="publishZero"
            fieldLabel={fieldLabel}
            onValueChange={handleValueChange}
            onValidityChange={isValid => onValidityChange?.(cellKey, isValid && rejectedValue === null)}
            externalError={rejectedValue !== null ? t('validation_must_be_at_least', { field: fieldLabel, min: 0 }) : null}
            resetKey={resetKey}
            variant="outlined"
            size="small"
            disabled={disabled}
            placeholder={placeholder}
            inputProps={inputProps}
        />
    );
};

export default EsalNumberCell;
